import React from "react";
import { Dialog } from "@mui/material";
import { brand, alpha } from "../../palette";

/**
 * Brand-themed modal wrapper. Uses MUI Dialog under the hood for focus
 * trap + escape handling, but the inner panel is fully Tailwind-styled
 * to match the editorial × lab-tech theme.
 *
 * Light: warm cream paper + ink + figures-rule borders.
 * Dark : warm graphite + cream + low-α cream borders. Same publication.
 */
export default function Modal({ open, onClose, children, maxWidth = "max-w-lg", scroll = "body" }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll={scroll}
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          margin: 0,
          maxWidth: "none",
          width: "100%",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          // Warm graphite scrim — matches dark mode bg, slightly transparent
          // in light to keep the page underneath legible.
          backgroundColor: alpha(brand.bg, 0.55),
          backdropFilter: "blur(8px)",
        },
        "& .MuiDialog-container": { padding: { xs: "16px", md: "32px" } },
      }}
    >
      <div
        className={`${maxWidth} mx-auto w-full bg-figures-wash dark:bg-brand-surface border border-figures-rule dark:border-white/10 rounded-md text-figures-ink dark:text-brand-displayInv shadow-2xl overflow-hidden`}
      >
        {children}
      </div>
    </Dialog>
  );
}

export function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="px-6 md:px-8 pt-6 pb-4 border-b border-figures-rule dark:border-white/10 flex items-start justify-between gap-4">
      <div>
        <h2 className="font-figuresSerif text-2xl md:text-3xl font-semibold tracking-tight text-figures-ink dark:text-brand-displayInv">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-figures-muted dark:text-brand-mutedInv mt-1">{subtitle}</p>
        )}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-figures-muted dark:text-brand-mutedInv hover:text-figures-ink dark:hover:text-brand-displayInv p-1 transition"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            viewBox="0 0 24 24"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function ModalBody({ children, className = "" }) {
  return <div className={`px-6 md:px-8 py-6 ${className}`}>{children}</div>;
}

export function ModalFooter({ children }) {
  return <div className="px-6 md:px-8 pb-6 pt-2 flex flex-col gap-3">{children}</div>;
}
